import { useEffect } from 'react';
import { useToast } from '@morressier/alembic';
import Loading from 'src/pages/Status/Loading';
import { fetchDraftSubmissionById, fetchFormSchema } from 'src/api/queries';
import useCurrentContextId from 'src/hooks/useCurrentContextId';
import { useLocation } from 'react-router-dom';
import { useFormContext } from 'src/contexts/FormContext';
import { getSourceFileSetSignature } from './fileSetSignature';

interface Props {
  organizationId: string;
  goToNextStep: () => void;
  goToStep: (step: number) => void;
  setDraftSubmissionId: (submissionId: string) => void;
}

const DraftSubmissionStep: React.FC<Props> = ({
  goToNextStep,
  goToStep,
  setDraftSubmissionId,
  organizationId
}) => {
  const ctxId = useCurrentContextId() ?? '';
  const location = useLocation();
  const {
    formData,
    updateFormData,
    setConsolidatedPdfSourceFileSignature
  } = useFormContext();
  const queryParams = new URLSearchParams(location.search);
  const draftId = queryParams.get('draftId');
  const toast = useToast();

  useEffect(() => {
    const updateFormWithDraft = async () => {
      if (draftId) {
        try {
          const draftData = await fetchDraftSubmissionById(ctxId, draftId);
          if (draftData) {
            const schemaData = await fetchFormSchema(
              ctxId,
              draftData.draft?.schemaId!
            );
            const updatedFormData = {
              ...draftData.draft,
              type: draftData.articleType,
              submissionContext: {
                submissionContextId: ctxId,
                organizationId: organizationId,
                articleWorkflowId: draftData?.articleWorkflowId,
                schema: schemaData.documentSchema,
                schemaId: draftData.draft?.schemaId!,
                articleTypeCategoryId: draftData.articleTypeCategoryId,
                documentType: draftData.articleType
              },
              hasRunExtraction: draftData.hasRunExtraction,
              isFileUpdated: draftData.isFileUpdated ?? false
            };
            const draftFileSet = updatedFormData.fileSet;
            setConsolidatedPdfSourceFileSignature(
              draftFileSet?.ConsolidatedPdf &&
                draftFileSet?.AnonymizedConsolidatedPdf
                ? getSourceFileSetSignature(draftFileSet)
                : null
            );
            updateFormData({
              ...formData,
              ...updatedFormData
            });
            goToStep(draftData.draftStep);
            setDraftSubmissionId(draftData.submissionId);
          } else {
            toast({
              variant: 'error',
              title: 'Draft Not Found',
              description:
                'Draft Not found. Start your submission process from beginning.',
              isClosable: true
            });
            goToNextStep();
          }
        } catch (error) {
          toast({
            variant: 'error',
            title: 'Error Fetching Draft',
            description:
              'An error occurred while fetching the draft. Please try again.',
            isClosable: true
          });
          goToNextStep();
        }
      }
    };
    if (!draftId) {
      goToNextStep();
    } else {
      updateFormWithDraft();
    }
  }, [draftId, fetchDraftSubmissionById]);

  return <Loading />;
};

export default DraftSubmissionStep;
